import { motion } from "motion/react";
import { useMotionEnabled } from "../../../../hooks/use-motion-enabled";
import {
  getBlockIdentifier,
  type CombinedContainer,
} from "../../../../lib/combine-blocks-and-containers";
import {
  BUTTON_REVEAL_OFFSET_Y,
  BUTTON_REVEAL_SCALE_FROM,
  BUTTON_REVEAL_TIMING,
  getButtonRevealDelayS,
  getRevealProps,
} from "../../../../lib/motion-presets";
import { MessageCard } from "./card";

type MessageGridCardsProps = {
  grid: CombinedContainer;
  messageId: string | undefined;
  /** The message's own entrance delay: the cascade starts when the message does. */
  revealDelayS?: number;
};

export const MessageGridCards = ({ grid, messageId, revealDelayS = 0 }: MessageGridCardsProps) => {
  const motionEnabled = useMotionEnabled();

  return (
    <div
      id={`message-${messageId}-grid-${grid.id}`}
      className="grid grid-cols-1 @xl/messages:grid-cols-2 @4xl/messages:grid-cols-3 gap-(--grid-gap) group/grid"
    >
      {grid.blocks.map((block, index) => {
        const blockIdentifier = getBlockIdentifier(block, index);
        return (
          // Deliberately the message buttons' cascade: a grid of cards is the same gesture,
          // a row of choices landing one after another. A grid arrives whole, so every card
          // is part of the same batch and the stagger runs from the first.
          //
          // The wrapper takes over the grid item role, so it carries what the card used to
          // get from the grid: a flex box filling its cell, or cards in the same row would
          // shrink to their own content instead of matching each other's height.
          <motion.div
            className="flex min-w-0 h-full"
            key={blockIdentifier}
            {...getRevealProps({
              motionEnabled,
              timing: BUTTON_REVEAL_TIMING,
              offsetY: BUTTON_REVEAL_OFFSET_Y,
              scaleFrom: BUTTON_REVEAL_SCALE_FROM,
              delayS: revealDelayS + getButtonRevealDelayS(index, 0),
              // Leaving retraces the arrival — down, shrinking, fading — but all together
              // rather than in cascade: the message holding the grid fades out in its own
              // 180ms, and a staggered departure longer than that would play out inside an
              // already invisible parent.
              withExit: true,
            })}
          >
            <MessageCard
              id={`message-${messageId}-grid-${grid.id}-block-${blockIdentifier}-card`}
              block={block as CombinedContainer}
              isInContainer
            />
          </motion.div>
        );
      })}
    </div>
  );
};
